import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { Button, Modal } from '@indipia/ui-core'
import {
  CUST_REQ_MESSAGE, custReqErrorMessage, useAdditionalDeliverCustReq, type CustReqItem, type CustReqMutationOptions,
} from '@indipia/shared-features'

// [반장님께 추가 전달] 버튼 + 확인 모달(R-24). 미전달 건이 있을 때만 그린다(호출부가 대상 목록을 넘김).
// 전달됨 전환은 서버가 발송 접수 성공 시에만 한다 — 실패(접수 거부 503·경합 409)는 오류 안내만 하고 화면 상태는 바꾸지 않는다.

interface Props {
  taskId: number
  /** 전달 대상(미전달 요청) — 비어 있으면 아무것도 그리지 않음 */
  targets: CustReqItem[]
  /** 받는 반장님 이름(공정의 현재 반장). 없으면 안내 문구로 대체 */
  foremanName?: string
  invalidate: CustReqMutationOptions
  /** 성공 후 호스트 캐시(간트 배지)를 다시 읽게 함 */
  onDelivered: () => void
  /** 경합(409) 시 목록을 다시 읽음 */
  onConflict: () => void
}

export function CustReqAdditionalDelivery({ taskId, targets, foremanName, invalidate, onDelivered, onConflict }: Props) {
  const [open, setOpen] = useState(false)
  const busyRef = useRef(false)
  const deliverM = useAdditionalDeliverCustReq(invalidate)

  if (targets.length === 0) return null

  const confirm = async () => {
    if (busyRef.current) return // 중복 클릭 방지
    busyRef.current = true
    try {
      const res = await deliverM.mutateAsync({
        taskId,
        items: targets.map(t => ({ custReqId: t.custReqId, updatedAt: t.updatedAt })),
      })
      toast.success(CUST_REQ_MESSAGE.addDeliveryDone(res.deliveredCount))
      setOpen(false)
      onDelivered()
    } catch (e) {
      const status = (e as { response?: { status?: number } } | null)?.response?.status
      if (status === 409) {
        toast.error(custReqErrorMessage(e, CUST_REQ_MESSAGE.addDeliveryConflict))
        setOpen(false)
        onConflict()
      } else {
        toast.error(custReqErrorMessage(e, CUST_REQ_MESSAGE.addDeliveryFail))
      }
    } finally { busyRef.current = false }
  }

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>{CUST_REQ_MESSAGE.addDelivery}</Button>
      <Modal
        open={open}
        onClose={() => { if (!deliverM.isPending) setOpen(false) }}
        title={CUST_REQ_MESSAGE.addDeliveryTitle}
        size="sm"
        footer={(
          <>
            <Button variant="secondary" disabled={deliverM.isPending} onClick={() => setOpen(false)}>{CUST_REQ_MESSAGE.cancel}</Button>
            <Button loading={deliverM.isPending} disabled={deliverM.isPending} onClick={() => { void confirm() }}>{CUST_REQ_MESSAGE.addDeliveryOk}</Button>
          </>
        )}
      >
        <p>{CUST_REQ_MESSAGE.addDeliveryCount(targets.length)}</p>
        <p className="mt-1">{foremanName ? CUST_REQ_MESSAGE.addDeliveryTo(foremanName) : CUST_REQ_MESSAGE.addDeliveryToUnknown}</p>
        <p className="mt-1" style={{ color: 'var(--shell-on-surface-2)' }}>{CUST_REQ_MESSAGE.addDeliveryNote}</p>
      </Modal>
    </>
  )
}
